/**
 * Publishing hub — Project Harmony
 *
 * Patch 34.2.1c · Now driven by the canonical CampaignRun projection
 * for every founder-facing counter (approved awaiting scheduling /
 * scheduled / published / blocked). The full Publishing workspace —
 * calendar, scheduled queue, published history, connection callout,
 * assets awaiting scheduling — is ALWAYS visible, even when no
 * platform is connected. The connection state is a callout, not a
 * paywall.
 */
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Calendar, Clock, Send, PackageOpen, Radio, AlertTriangle } from "lucide-react";

import { PageHeader } from "@/shared/ui/page-header";
import { Button } from "@/shared/ui/button";
import { useWorkspaceMilestones } from "@/entities/workspace/milestones";
import { useCurrentWorkspace } from "@/entities/workspace";
import { useCampaignRunSummary } from "@/entities/campaign-run";
import { listConnections, type PublishingConnection } from "@/entities/publishing";

const TABS = [
  { to: "/publishing/calendar",  label: "Publishing Calendar", body: "Every scheduled post laid out by day.", icon: Calendar },
  { to: "/publishing/scheduled", label: "Scheduled",           body: "Posts queued to publish.",              icon: Clock    },
  { to: "/publishing/published", label: "Published",           body: "Everything you've shipped.",            icon: Send     },
  { to: "/publishing/platforms", label: "Connected Platforms", body: "Where Orkexra may publish.",         icon: Radio    },
  { to: "/publishing/history",   label: "Publishing History",  body: "Full audit trail.",                     icon: PackageOpen },
] as const;

export default function PublishingHubPage(): JSX.Element {
  const workspace = useCurrentWorkspace();
  const m = useWorkspaceMilestones(workspace?.id ?? null);
  const runSummary = useCampaignRunSummary(workspace?.id ?? null);
  const connectionsQ = useQuery({
    queryKey: ["publishing", workspace?.id],
    queryFn: () => listConnections(workspace!.id),
    enabled: !!workspace,
    retry: false,
  });

  const canonical = runSummary.data?.counters;
  const canonicalReady = !!workspace && !runSummary.isLoading;
  const summaryError = runSummary.isError;

  // Canonical counters — the only source of truth for the founder-
  // facing headline numbers.
  const approvedReady = canonical?.approved ?? 0;
  const scheduled = canonical?.scheduled ?? 0;
  const published = canonical?.published ?? 0;
  const failed = canonical?.failed ?? 0;

  // Blocked / unhealthy — anything the projection reports outside the
  // healthy/generating buckets. These are runs the founder needs to see.
  const runsByHealth = runSummary.data?.runs_by_health ?? {};
  const unhealthyRuns = (Object.entries(runsByHealth) as [string, number][])
    .filter(([k]) => k !== "healthy" && k !== "generating")
    .reduce((acc, [, n]) => acc + (n ?? 0), 0);

  // Connections — used to shape the "approved-ready" callout and to
  // decide whether to hint at "Connect a platform".
  const connections: PublishingConnection[] = connectionsQ.data ?? [];
  const anyConnected = connections.some(
    (c) => c.status === "connected" || c.status === "sandbox",
  );
  const connectionsReady = !connectionsQ.isLoading;

  return (
    <div className="mx-auto max-w-[1120px] px-4 py-8 md:px-8 md:py-12" data-testid="publishing-hub">
      <PageHeader
        eyebrow="Publishing"
        title="Publishing"
        subtitle="Review what is scheduled, adjust your calendar, and keep every connected platform moving."
      />

      {/* Patch 34.2.1c · Canonical projection unavailable — recovery
          state. Never silently swaps in stitched milestone numbers. */}
      {summaryError && (
        <div
          data-testid="publishing-summary-error"
          className="mb-6 rounded-xl border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm"
        >
          <div className="font-semibold text-red-800">
            Canonical Publishing counters unavailable.
          </div>
          <div className="text-xs text-red-900/80 mt-1">
            Couldn&apos;t reach the CampaignRun projection. Tabs below still
            work. Refresh, or check backend health.
          </div>
        </div>
      )}

      {/* Approved-ready callout · appears when the founder has approved
          content but no platform is connected. Replaces the old
          "no first blueprint" empty state — the workspace is never
          hidden. */}
      {canonicalReady && connectionsReady && approvedReady > 0 && !anyConnected && (
        <div
          data-testid="publishing-approved-ready"
          className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/10 px-4 py-3"
        >
          <div className="text-sm">
            <div className="font-semibold text-[var(--md-ivory-0)]" data-testid="publishing-approved-ready-count">
              {approvedReady} approved asset{approvedReady === 1 ? " is" : "s are"} ready.
            </div>
            <div className="text-[12px] text-[color:var(--md-fog,#a0a4ac)]">
              Connect a platform to schedule them.
            </div>
          </div>
          <Button asChild size="sm" data-testid="publishing-approved-ready-cta">
            <Link to="/publishing/platforms">Connect a Platform</Link>
          </Button>
        </div>
      )}

      {/* Blocked / unhealthy runs · always visible when the projection
          reports them. */}
      {canonicalReady && (unhealthyRuns > 0 || failed > 0) && (
        <div
          data-testid="publishing-blocked"
          className="mb-6 flex flex-wrap items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 text-red-500" aria-hidden />
          <div className="flex-1">
            <div className="font-semibold text-red-800" data-testid="publishing-blocked-count">
              {unhealthyRuns > 0
                ? `${unhealthyRuns} campaign${unhealthyRuns === 1 ? "" : "s"} need${unhealthyRuns === 1 ? "s" : ""} attention`
                : `${failed} failed asset${failed === 1 ? "" : "s"}`}
            </div>
            <div className="text-xs text-red-900/80">
              Publishing is paused for anything unhealthy until the blocker clears.
            </div>
          </div>
          <Button asChild size="sm" variant="outline" data-testid="publishing-blocked-cta">
            <Link to="/campaigns">Open Campaigns</Link>
          </Button>
        </div>
      )}

      {/* Canonical Publishing counters — the ONE source of truth. */}
      {canonicalReady && (
        <div
          data-testid="publishing-counters"
          className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4"
        >
          <PublishingCounter label="Approved" value={approvedReady} tone="amber" testId="publishing-counter-approved" />
          <PublishingCounter label="Scheduled" value={scheduled} tone="sky" testId="publishing-counter-scheduled" />
          <PublishingCounter label="Published" value={published} tone="emerald" testId="publishing-counter-published" />
          <PublishingCounter label="Failed" value={failed} tone={failed > 0 ? "red" : "muted"} testId="publishing-counter-failed" />
        </div>
      )}

      {/* Full Publishing workspace — ALWAYS visible, even with zero
          connections. The connection tile below morphs into a
          "Connect a platform" nudge when nothing is connected. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="publishing-tabs">
        {TABS.map((t) => {
          const isPlatforms = t.to === "/publishing/platforms";
          // The Connect-a-Platform nudge only replaces the "Connected
          // Platforms" tile visually — the tile itself is still there
          // so the founder can enter the tab.
          if (isPlatforms && connectionsReady && !anyConnected) {
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid="publishing-tab-platforms"
                className="rounded-xl border border-dashed border-[color:var(--md-amber)]/40 bg-[var(--md-amber)]/5 p-5 transition hover:border-[var(--md-amber)]"
              >
                <t.icon className="h-5 w-5 text-[var(--md-amber)]" aria-hidden />
                <p className="mt-3 text-sm font-semibold text-[var(--md-ivory-0)]">Connect a platform</p>
                <p className="mt-1 text-xs text-[var(--md-fog)]">
                  Connect Facebook, Instagram, LinkedIn, or TikTok to allow your Publishing Team to publish automatically.
                </p>
              </Link>
            );
          }
          return (
            <Link
              key={t.to}
              to={t.to}
              data-testid={`publishing-tab-${t.to.split("/").pop()}`}
              className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-5 transition hover:border-[var(--md-amber)]/50 hover:bg-[var(--md-ink-1)]"
            >
              <t.icon className="h-5 w-5 text-[var(--md-amber)]" aria-hidden />
              <p className="mt-3 text-sm font-semibold text-[var(--md-ivory-0)]">{t.label}</p>
              <p className="mt-1 text-xs text-[var(--md-fog)]">{t.body}</p>
            </Link>
          );
        })}
      </div>

      {/* Integrity Mismatch — canonical vs milestone rollup for
          scheduled/published. Uses the exact same never-flash guard
          pattern as Vault + Review. */}
      {canonicalReady && !m.isLoading && (
        <PublishingIntegrity
          canonicalScheduled={scheduled}
          canonicalPublished={published}
          inventoryScheduled={m.scheduledCount}
          inventoryPublished={m.publishedCount}
        />
      )}
    </div>
  );
}

interface PublishingCounterProps {
  label: string;
  value: number;
  tone: "amber" | "emerald" | "sky" | "indigo" | "muted" | "red";
  testId: string;
}

function PublishingCounter({ label, value, tone, testId }: PublishingCounterProps): JSX.Element {
  const toneClass = {
    amber:   "text-[var(--md-amber)]",
    emerald: "text-emerald-400",
    sky:     "text-sky-400",
    indigo:  "text-indigo-400",
    muted:   "text-[var(--md-fog)]",
    red:     "text-red-500",
  }[tone];
  return (
    <div
      data-testid={testId}
      className="rounded-xl border border-[color:var(--md-line)] bg-[var(--md-ink-1)]/40 p-4"
    >
      <p className="text-xs uppercase tracking-widest text-[var(--md-fog)]">{label}</p>
      <p className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

/** Integrity Mismatch guardrail for Publishing. */
function PublishingIntegrity({
  canonicalScheduled,
  canonicalPublished,
  inventoryScheduled,
  inventoryPublished,
}: {
  canonicalScheduled: number;
  canonicalPublished: number;
  inventoryScheduled: number;
  inventoryPublished: number;
}): JSX.Element | null {
  const scheduledMismatch = canonicalScheduled !== inventoryScheduled;
  const publishedMismatch = canonicalPublished !== inventoryPublished;
  if (!scheduledMismatch && !publishedMismatch) return null;
  return (
    <div
      data-testid="publishing-integrity-mismatch"
      className="mt-6 rounded-xl border border-red-500/60 bg-red-500/10 px-4 py-3 text-sm"
    >
      <div
        className="font-semibold text-red-800"
        data-testid="publishing-integrity-mismatch-title"
      >
        Integrity mismatch — Publishing inventory disagrees with canonical projection.
      </div>
      <ul className="mt-2 space-y-0.5 text-xs text-red-900/80">
        {scheduledMismatch && (
          <li data-testid="publishing-integrity-mismatch-scheduled">
            Scheduled: canonical <strong>{canonicalScheduled}</strong> vs
            inventory <strong>{inventoryScheduled}</strong>
          </li>
        )}
        {publishedMismatch && (
          <li data-testid="publishing-integrity-mismatch-published">
            Published: canonical <strong>{canonicalPublished}</strong> vs
            inventory <strong>{inventoryPublished}</strong>
          </li>
        )}
      </ul>
      <p className="mt-2 text-xs text-red-900/80">
        One of the two is stale. Refresh, or check reconciliation.
      </p>
    </div>
  );
}
